// Maps backend ``detail`` strings (the FastAPI ``HTTPException(detail=…)``
// payload) to user-friendly i18n keys. Covers the new failure modes
// surfaced by the docgen-tooling work — reference attach + primary
// document validation. Older codes (credential_unavailable, worktree
// caps, attachment_too_large, …) keep their inline handling in
// ``SessionView``'s ``formatSendError`` for now.
//
// Unknown codes fall through to the raw detail so debuggability isn't
// lost in dev / staging. Pure function — no React deps, easy to unit
// test against the bundled i18n instance.

import type { TFunction } from "i18next";

import { extractApiDetail } from "@/lib/apiDetail";

export const ERROR_KEY_MAP: Record<string, string> = {
  // Invitation lifecycle codes (org_invitations.py raises exc.code).
  invitation_already_pending: "api_errors.invitation_already_pending",
  invitation_not_found: "api_errors.invitation_not_found",
  already_accepted: "api_errors.invitation_already_accepted",
  revoked: "api_errors.invitation_revoked",
  expired: "api_errors.invitation_expired",
  invalid_role: "api_errors.invalid_role",
  // Org managers run Members below admin (deps.assert_manager_within_bounds).
  manager_cannot_grant_admin: "api_errors.manager_cannot_grant_admin",
  manager_cannot_change_admin: "api_errors.manager_cannot_change_admin",
  // A platform admin acting above their own row (deps.assert_may_grant_membership).
  platform_admin_cannot_grant_admin: "api_errors.platform_admin_cannot_grant_admin",
  // Last-admin safeguard (orgs._assert_not_last_admin): the backend sends
  // these sentences as the detail, for every actor including platform admins.
  // (Leaving an org has its own sentence, but the SPA offers no leave.)
  "cannot demote the last admin": "api_errors.last_admin_demote",
  "cannot remove the last admin": "api_errors.last_admin_remove",
  // Code-source setup links (FW-32).
  too_many_setup_links: "api_errors.too_many_setup_links",
  setup_link_used: "api_errors.setup_link_used",
  setup_link_invalid: "api_errors.setup_link_invalid",
  setup_link_not_found: "api_errors.setup_link_not_found",
  setup_link_write_not_allowed: "api_errors.setup_link_write_not_allowed",
  setup_link_wrong_host: "api_errors.setup_link_wrong_host",
  setup_links_disabled: "api_errors.setup_links_disabled",
  // A host-key rescan that scanned nothing used to return 200 with
  // the unchanged row, so a no-op looked exactly like a fix.
  ssh_keyscan_failed: "api_errors.ssh_keyscan_failed",
  // Code-source create/update: an Azure DevOps Services URL without the
  // organization segment (``https://dev.azure.com``) is rejected 422.
  azure_devops_organization_required:
    "api_errors.azure_devops_organization_required",
  // Code-source create/save refusals (code_sources.py ``_err``): these
  // reached the dialog as raw codes.
  blocked_host: "api_errors.blocked_host",
  slug_taken: "api_errors.slug_taken",
  too_many_sources: "api_errors.too_many_sources",
  bitbucket_workspace_required: "api_errors.bitbucket_workspace_required",
  invalid_slug: "api_errors.invalid_slug",
  // Code-source delete raced a project import (the FK still held): retry.
  code_source_in_use: "api_errors.code_source_in_use",
  // Deleting the root org's GitLab source from the server configuration.
  code_source_env_managed: "api_errors.code_source_env_managed",
  // Send on a session whose project was removed with its code source.
  project_detached: "session.error.projectDetached",
  // PATCH /auth/me/preferences: not a UI language the registry ships.
  ui_language_unsupported: "api_errors.ui_language_unsupported",
  // Session create/PATCH: not an output language the registry ships.
  language_unsupported: "api_errors.language_unsupported",
  // Storage exhaustion (DB or filesystem): backend returns 503 with
  // ``detail: "disk_full"``. Mapped here so every call site routing
  // through ``translateApiError`` surfaces the actionable copy instead
  // of the raw code; ``formatSendError`` checks it ahead of its 5xx
  // catch-all so a 503 doesn't get hijacked by the generic server error.
  disk_full: "session.error.diskFull",
  // A job route (brand / prompt-fragment composer) refused a user with no
  // usable AI account before queueing anything (409, GitLab #45).
  no_ai_account: "api_errors.no_ai_account",
  // OpenAI didn't answer the account-link token exchange in time (424).
  openai_link_timeout: "api_errors.openai_link_timeout",
  // The edge's 503 while a deploy restarts the backend (deploy/Caddyfile
  // ``handle_errors``) — any request can hit it, not just a send.
  "maintenance_mode:restarting": "maintenance.restarting_title",
  reference_forbidden: "session.error.referenceForbidden",
  parent_document_not_visible: "session.error.parentNotVisible",
  parent_document_not_found: "session.error.parentNotFound",
  missing_primary_document: "session.error.missingPrimaryDocument",
  primary_document_format_mismatch: "session.error.primaryDocumentFormatMismatch",
  primary_document_not_viewable: "session.error.primaryDocumentNotViewable",
  primary_only_for_modify: "session.error.primaryOnlyForModify",
  // Org-policy gates (2026-09-01): ex-members are read-only; working
  // repos must belong to the session's org.
  org_membership_required: "session.error.orgMembershipRequired",
  // Org routes (deps.py guards, members, brand, LDAP, configuration):
  // stable codes instead of the English sentences they used to send.
  org_not_found: "api_errors.org.not_found",
  org_not_member: "api_errors.org.not_member",
  "org_role_required:member": "api_errors.org.role_required_member",
  "org_role_required:manager": "api_errors.org.role_required_manager",
  "org_role_required:admin": "api_errors.org.role_required_admin",
  org_admin_needs_active_org: "api_errors.org.admin_needs_active_org",
  org_admin_or_manager_required: "api_errors.org.admin_or_manager_required",
  root_org_undeletable: "api_errors.org.root_undeletable",
  org_delete_blocked: "api_errors.org.delete_blocked",
  already_member: "api_errors.org.already_member",
  membership_not_found: "api_errors.org.membership_not_found",
  brand_token_key_required: "api_errors.brand.token_key_required",
  brand_token_not_set: "api_errors.brand.token_not_set",
  brand_asset_kind_unknown: "api_errors.brand.asset_kind_unknown",
  brand_asset_not_set: "api_errors.brand.asset_not_set",
  brand_asset_too_large: "api_errors.brand.asset_too_large",
  brand_document_not_found: "api_errors.brand.document_not_found",
  brand_document_title_required: "api_errors.brand.document_title_required",
  brand_document_type_not_allowed: "api_errors.brand.document_type_not_allowed",
  brand_document_too_large: "api_errors.brand.document_too_large",
  ldap_bind_dn_required: "api_errors.ldap.bind_dn_required",
  ldap_server_url_invalid: "api_errors.ldap.server_url_invalid",
  ldap_host_not_allowed: "api_errors.ldap.host_not_allowed",
  ldap_not_configured: "api_errors.ldap.not_configured",
  unknown_setting: "api_errors.unknown_setting",
  rule_not_found: "api_errors.rule_not_found",
  repo_org_mismatch: "session.error.repoOrgMismatch",
  // A document from another org than the session's: attach, reference,
  // edit or iterate on (sessions PATCH/create, send, render).
  document_org_mismatch: "session.error.documentOrgMismatch",
  // Linking a project in the session's Context tab (PATCH /sessions/{id}
  // add/remove/promote/editable refs, branch switch). These reached the
  // tab's error banners as raw codes.
  code_source_org_forbidden: "session.error.codeSourceOrgForbidden",
  repository_org_forbidden: "session.error.codeSourceOrgForbidden",
  repository_not_found: "session.error.repositoryNotFound",
  editable_ref_missing_branch: "session.error.editableRefMissingBranch",
  editable_refs_need_write_intent: "session.error.editableRefsNeedWriteIntent",
  turn_in_progress: "session.error.turnInProgress",
  promote_missing_repository: "session.error.promoteMissingRepository",
  // Session view cards: the work draft, the workspace tree, a pending
  // approval whose turn already ended.
  draft_missing_from_worktree: "session.work_draft_no_draft",
  // Saving a modify draft that isn't OOXML (a .doc or RTF renamed .docx).
  draft_not_ooxml: "session.work_draft_not_ooxml",
  no_primary_attachment: "session.error.noPrimaryAttachment",
  "session:no_active_runner": "session.error.noActiveRunner",
  path_not_in_workspace: "session.error.workspacePathGone",
  path_is_not_a_directory: "session.error.workspacePathGone",
  path_is_directory: "session.error.workspacePathGone",
  encryption_not_configured: "session.error.codeSourcesNotConfigured",
  gitlab_not_configured: "session.error.codeSourcesNotConfigured",
  // The legacy ``gitlab_project_id`` field under per-org DAC with no single
  // root GitLab code source on RW_GITLAB_URL to import it through.
  legacy_project_source_unresolved: "session.error.codeSourcesNotConfigured",
  // Brand profiles: the org-brand, compose and session routes.
  brand_profile_not_found: "admin.brand.profiles.error_not_found",
  brand_profile_name_taken: "admin.brand.profiles.error_name_taken",
  brand_profile_name_required: "admin.brand.profiles.error_name_required",
  brand_profile_primary_delete: "admin.brand.profiles.delete_primary_blocked",
  brand_profile_env_managed: "admin.brand.profiles.delete_env_blocked",
  invalid_target_mode: "admin.brand.profiles.error_invalid_target_mode",
  // Copying a profile whose image bank passes the org's storage ceiling.
  brand_image_quota_org: "admin.brand.profiles.error_images_over_quota",
  // Brand profile from an uploaded template (org_brand_template.py).
  template_too_large: "admin.brand.template.errors.too_large",
  template_empty: "admin.brand.template.errors.empty",
  template_not_ooxml: "admin.brand.template.errors.not_ooxml",
  template_macro_enabled: "admin.brand.template.errors.macro_enabled",
  template_unsupported_type: "admin.brand.template.errors.unsupported_type",
  template_too_many_parts: "admin.brand.template.errors.too_big_unpacked",
  template_too_large_unpacked: "admin.brand.template.errors.too_big_unpacked",
  template_too_complex: "admin.brand.template.errors.too_big_unpacked",
  template_unreadable: "admin.brand.template.errors.unreadable",
  template_changed: "admin.brand.template.errors.changed",
  template_length_required: "admin.brand.template.errors.length_required",
  template_form_invalid: "admin.brand.template.errors.form_invalid",
  // Prompt fragments: a style may target only an intent that accepts
  // styles (create + Draft with AI).
  intents_invalid: "api_errors.intents_invalid",
  // Prompt library: make a copy / rebase (org_prompt_fragments.py).
  platform_fragment_not_found: "api_errors.platform_fragment_not_found",
  platform_version_moved: "api_errors.platform_version_moved",
  fragment_not_derived: "api_errors.fragment_not_derived",
  fragment_source_required: "api_errors.fragment_source_required",
  fragment_changed: "api_errors.fragment_changed",
  fragment_model_scoped: "api_errors.fragment_model_scoped",
  // Custom LLM providers (app/api/llm_providers.py, service.py, urls.py,
  // upstream.py). "rate_limit_exceeded" is NOT mapped here: it's a
  // shared code with different meanings per surface, handled locally
  // by LlmProvidersTab instead.
  llm_providers_disabled: "api_errors.llm_provider.disabled",
  // Test or Delete on a row someone else just deleted: the row is
  // reachable (it's still in the caller's own list render), but a 404
  // on acting on it. LlmProvidersTab also invalidates the list query
  // on this code so the stale row drops off screen.
  llm_provider_not_found: "api_errors.llm_provider.not_found",
  llm_provider_name_taken: "api_errors.llm_provider.name_taken",
  llm_provider_too_many: "api_errors.llm_provider.too_many",
  llm_provider_invalid_base_url: "api_errors.llm_provider.invalid_base_url",
  llm_provider_https_required: "api_errors.llm_provider.https_required",
  llm_provider_base_url_credentials: "api_errors.llm_provider.base_url_credentials",
  // A base URL whose path holds something shaped like an API key.
  llm_provider_base_url_secret: "api_errors.llm_provider.base_url_secret",
  llm_provider_blocked_address: "api_errors.llm_provider.blocked_address",
  llm_provider_dns_resolution_failed: "api_errors.llm_provider.dns_resolution_failed",
  llm_provider_no_models: "api_errors.llm_provider.no_models",
  llm_provider_too_many_models: "api_errors.llm_provider.too_many_models",
  llm_provider_invalid_model_slug: "api_errors.llm_provider.invalid_model_slug",
  llm_provider_duplicate_model_slug: "api_errors.llm_provider.duplicate_model_slug",
  llm_provider_invalid_key: "api_errors.llm_provider.invalid_key",
  llm_provider_invalid_header: "api_errors.llm_provider.invalid_header",
  llm_provider_duplicate_header: "api_errors.llm_provider.duplicate_header",
  llm_provider_forbidden_header: "api_errors.llm_provider.forbidden_header",
  llm_provider_too_many_headers: "api_errors.llm_provider.too_many_headers",
  llm_provider_private_network_platform_only: "api_errors.llm_provider.private_network_platform_only",
  llm_provider_invalid_name: "api_errors.llm_provider.invalid_name",
  llm_provider_invalid_scope: "api_errors.llm_provider.invalid_scope",
  llm_provider_invalid_api_format: "api_errors.llm_provider.invalid_api_format",
  // A session's custom LLM provider can't run (FW-19 phase 2). Create
  // answers 422 ``llm_provider_unavailable:<reason>``; ``/summary``
  // answers 409 with ``…:summary_unsupported`` (see Task 11 brief and
  // ``app/services/llm_providers/session_binding.py``).
  "llm_provider_unavailable:disabled": "api_errors.llm_provider.unavailable",
  "llm_provider_unavailable:not_found": "api_errors.llm_provider.unavailable",
  "llm_provider_unavailable:not_visible": "api_errors.llm_provider.unavailable",
  "llm_provider_unavailable:model_unavailable": "api_errors.llm_provider.unavailable",
  "llm_provider_unavailable:cli_unavailable": "api_errors.llm_provider.unavailable",
  "llm_provider_unavailable:model_required": "api_errors.llm_provider.model_required",
  "llm_provider_unavailable:unsupported_format": "api_errors.llm_provider.unsupported",
  // FW-19 phase 3: a session on a local model refused at send.
  "llm_provider_unavailable:local_owner_only": "api_errors.llm_provider.local_owner_only",
  "llm_provider_unavailable:local_offline": "api_errors.llm_provider.local_offline",
  // Every session works through tool calls: a model that can't call
  // tools, or a local one that hasn't passed Test (``check_model_tools``).
  "llm_provider_unavailable:model_no_tools": "api_errors.llm_provider.model_no_tools",
  "llm_provider_unavailable:local_model_untested": "api_errors.llm_provider.local_model_untested",
  "llm_provider_unavailable:cli_mismatch": "api_errors.llm_provider.unsupported",
  "llm_provider_unavailable:summary_unsupported": "api_errors.llm_provider.summary_unsupported",
  // The Account page's local-model connector routes
  // (``app/api/llm_connector.py``).
  llm_connector_invalid_name: "api_errors.llm_connector.invalid_name",
  llm_connector_too_many_tokens: "api_errors.llm_connector.too_many_tokens",
  llm_connector_not_found: "api_errors.llm_connector.not_found",
  // PATCH chat_runtime_mode=proxy_tui on a custom-provider session (409);
  // the terminal WS refuses with the same code as its close reason.
  llm_provider_tui_unsupported: "session.tui.llm_provider_unsupported_title",
};

// Codes whose ``detail`` carries a dynamic tail (an id, a nested
// reason), matched by prefix after the exact map. The session service
// wraps the fragment validator as
// ``prompt_fragment_invalid:fragment_not_in_org:<id>``.
export const ERROR_PREFIX_MAP: ReadonlyArray<[prefix: string, key: string]> = [
  ["prompt_fragment_invalid:", "api_errors.prompt_fragment_invalid"],
  // Context-tab ref edits: ``editable_cap:have=N cap=M``, and a branch
  // switch to a branch the host doesn't have (``branch_not_found:<name>
  // does not exist on …`` — an English sentence after the code).
  // Session create: the user's preferred OpenAI account can't run a turn
  // (``credential_unavailable:preferred_account_unusable:<reason>``).
  [
    "credential_unavailable:preferred_account_unusable",
    "session.error.preferredAccountUnusableCreate",
  ],
  // Send-time refusal of a session's custom LLM provider — the per-turn
  // re-check wraps the reason the same way create's 422 does (Task 8).
  // Before the generic ``credential_unavailable`` entry below: the
  // first matching prefix wins.
  ["credential_unavailable:llm_provider_unavailable", "session.error.llmProviderUnavailable"],
  // Any other unusable credential on create (``account_refresh_failed``,
  // ``account_status:<status>``, ``no_account``…). After the specific
  // entry above: the first matching prefix wins.
  ["credential_unavailable", "session.error.credentialUnavailable"],
  // OpenAI refused or broke the sign-in code exchange
  // (``openai_oauth:token_exchange_failed:<status>:<OpenAI's body>``,
  // ``state_mismatch``, ``unknown_handle``…): never show OpenAI's body.
  ["openai_oauth:", "api_errors.openai_link_failed"],
  ["editable_cap:", "session.error.editableCap"],
  ["branch_not_found:", "session.error.branchNotFound"],
  ["internal_doc_ref_not_viewable:", "session.error.internalDocRefNotViewable"],
  // Resolving a picked project through its code source (add-project):
  // the token was refused, the host couldn't find the project, or the
  // source is gone. The tail is the provider's own (English) text.
  ["provider_auth_failed:", "session.error.providerAuthFailed"],
  ["provider_lookup_failed:", "session.error.providerLookupFailed"],
  ["gitlab_fetch_failed:", "session.error.providerLookupFailed"],
  ["unknown_code_source:", "session.error.unknownCodeSource"],
  // Saving the work draft: the DOCX → PDF conversion failed.
  ["render_failed:", "session.error.renderFailed"],
  // A template the reader refused, naming the part it choked on.
  ["template_part_too_large:", "admin.brand.template.errors.too_big_unpacked"],
  ["template_malformed:", "admin.brand.template.errors.malformed"],
  // A brand asset whose file type the slot doesn't take (``:<kind>``).
  ["brand_asset_type_not_allowed:", "api_errors.brand.asset_type_not_allowed"],
];

export function translateApiError(
  t: TFunction,
  detail: string | undefined | null,
): string {
  if (!detail) return "";
  const key =
    ERROR_KEY_MAP[detail] ??
    ERROR_PREFIX_MAP.find(([prefix]) => detail.startsWith(prefix))?.[1];
  if (key) {
    return t(key);
  }
  return detail; // unknown error code → show raw for debuggability
}


/** One call for "show this failed request to a person": pull the
 *  backend detail (string or object shape) via ``extractApiDetail``,
 *  translate known codes, and fall back to the raw detail — never the
 *  transport's "Request failed with status code NNN". */
export function apiErrorText(t: TFunction, err: unknown): string {
  const detail = extractApiDetail(err);
  return translateApiError(t, detail) || detail;
}

/** Like ``apiErrorText``, but for a surface that must never show a raw
 *  backend code or the backend's English sentence: a known code is
 *  translated, anything else becomes ``fallback`` (the caller's own
 *  translated "could not …" line). */
export function knownApiErrorText(
  t: TFunction,
  err: unknown,
  fallback: string,
): string {
  const detail = extractApiDetail(err);
  const text = translateApiError(t, detail);
  return text && text !== detail ? text : fallback;
}

/** ``apiErrorText`` for an entry point that starts an agent run outside a
 *  session (the brand and prompt-fragment composers). Their 409s share
 *  session create's codes, but session create's wording ("so the session
 *  was not created") is wrong there, so the run-neutral codes get their
 *  own text first. */
export function runErrorText(t: TFunction, err: unknown): string {
  const detail = extractApiDetail(err);
  if (detail.startsWith("credential_unavailable:preferred_account_unusable")) {
    return t("api_errors.preferred_account_unusable_run");
  }
  return apiErrorText(t, err);
}

/** Error text for the account-link surfaces (link, re-authenticate):
 *  a known code is translated, anything else — a provider's raw response
 *  body, the transport's English "Request failed with status code …" —
 *  becomes the translated "Linking the account didn't work". */
export function linkErrorText(t: TFunction, err: unknown): string {
  return knownApiErrorText(t, err, t("api_errors.link_failed"));
}
